import React from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {QUERY_CACHE_KEYS, CartPayment} from '@chekin/core';
import {API} from '@chekin/api';
import {usePaymentsEnv} from '../PaymentsEnv';
import {usePaymentsStore} from '../store';
import {usePreloadDeferredPayments} from './usePreloadDeferredPayments';

const CACHE_TIME = 5_000;
const STALE_TIME = 5_000;

function useDeferredPayments(defaultDeferredPayments?: CartPayment[]) {
  const deferredPayments = usePaymentsStore(state => state.deferredPayments);
  const addDeferredPayments = usePaymentsStore(state => state.addDeferredPayments);
  const removeDeferredPayment = usePaymentsStore(state => state.removeDeferredPayment);

  const deferredPaymentsIds = React.useMemo(() => {
    return deferredPayments.map(p => p.id);
  }, [deferredPayments]);

  React.useEffect(
    function setDefaultPayments() {
      if (!deferredPayments?.length && defaultDeferredPayments?.length) {
        addDeferredPayments(defaultDeferredPayments);
      }
    },
    [addDeferredPayments, defaultDeferredPayments, deferredPayments?.length],
  );

  return {
    deferredPayments,
    deferredPaymentsIds,
    addDeferredPayments,
    removeDeferredPayment,
  };
}

type UsePaymentsCartProps = {
  refetchOnMount?: boolean | 'always';
  refetchOnWindowFocus?: boolean;
  defaultDeferredPayments?: CartPayment[];
  enabled?: boolean;
};
function usePaymentsCart({
  refetchOnMount = true,
  refetchOnWindowFocus = false,
  defaultDeferredPayments,
  enabled = true,
}: UsePaymentsCartProps = {}) {
  const {isTokenValid, reservationId} = usePaymentsEnv();
  const {deferredPaymentsIds, addDeferredPayments, removeDeferredPayment} =
    useDeferredPayments(defaultDeferredPayments);
  const {isPreloading} = usePreloadDeferredPayments({
    addDeferredPayments,
    removeDeferredPayment,
  });

  const {
    data: paymentsCart,
    isLoading: isPaymentsCartLoading,
    isFetching: isPaymentsCartFetching,
  } = useQuery({
    queryKey: [QUERY_CACHE_KEYS.paymentsCart, deferredPaymentsIds, reservationId],
    queryFn: () =>
      API.payments.getPaymentsCart(
        `reservation=${reservationId}&pay_later=${deferredPaymentsIds?.toString()}`,
      ),
    placeholderData: keepPreviousData,
    enabled: Boolean(!isPreloading && reservationId && isTokenValid && enabled),
    refetchOnWindowFocus,
    refetchOnMount,
    gcTime: CACHE_TIME,
    staleTime: STALE_TIME,
  });

  const selectedPayments = paymentsCart?.pay_now_payments || [];
  const isPaymentsCartResolved = Boolean(paymentsCart);
  const isPaymentsCartEmpty =
    isPaymentsCartResolved &&
    !paymentsCart?.pay_now_payments?.length &&
    !paymentsCart?.pay_later_payments?.length &&
    !isPaymentsCartFetching;

  return {
    paymentsCart,
    isPaymentsCartLoading,
    isPaymentsCartFetching,
    isPaymentsCartResolved,
    selectedPayments,
    isPaymentsCartEmpty,
    addDeferredPayments,
    removeDeferredPayment,
  };
}

export {usePaymentsCart};
